import { FC, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Grid,
  Typography,
  Divider,
  Switch,
  FormControlLabel,
} from '@mui/material';
import StructuredModal from 'src/views/components/structuredModal/structuredModal';
import ControlledTextField from 'src/views/components/controlledComponents/ControlledTextField.component';
import ControlledSelectField from 'src/views/components/controlledComponents/ControlledSelectField.component';
import { useTranslation } from 'src/hooks/useTranslationHook';
import {
  useHealthAndSafetyState,
  useHealthAndSafetyActions,
  EViewState,
} from '../../../../contexts/healthAndSafety.context';
import { useHealthAndSafety } from '../../../../hooks/useHealthAndSafety';
import { TEmergencyContact } from 'src/apis/administration/administration.interfaces';
import { TKeyValue } from 'src/utils/commonInterfaces';

type EmergencyContactFormData = {
  name: string;
  relationship:
  | 'PARENT'
  | 'GUARDIAN'
  | 'EMERGENCY_CONTACT'
  | 'DOCTOR'
  | 'HOSPITAL';
  phoneNumber: string;
  alternatePhoneNumber?: string;
  email?: string;
  address?: string;
  isActive: boolean;
  priority: number;
  personId: string;
  personType: 'STUDENT' | 'STAFF';
};

const getEmergencyContactFormSchema = (
  translateText: (key: string) => string
) =>
  z.object({
    name: z.string().min(1, translateText('common.fieldRequired')),
    relationship: z.enum(
      ['PARENT', 'GUARDIAN', 'EMERGENCY_CONTACT', 'DOCTOR', 'HOSPITAL'],
      {
        required_error: translateText('common.fieldRequired'),
      }
    ),
    phoneNumber: z
      .string()
      .min(1, translateText('common.fieldRequired'))
      .regex(
        /^[0-9+\-\s()]+$/,
        translateText(
          'administration.healthAndSafety.validation.invalidPhoneNumber'
        )
      ),
    alternatePhoneNumber: z
      .string()
      .regex(
        /^[0-9+\-\s()]*$/,
        translateText(
          'administration.healthAndSafety.validation.invalidPhoneNumber'
        )
      )
      .optional()
      .or(z.literal('')),
    email: z
      .string()
      .email(
        translateText('administration.healthAndSafety.validation.invalidEmail')
      )
      .optional()
      .or(z.literal('')),
    address: z.string().optional(),
    isActive: z.boolean(),
    priority: z
      .number()
      .min(
        1,
        translateText('administration.healthAndSafety.validation.priorityMin')
      )
      .max(
        10,
        translateText('administration.healthAndSafety.validation.priorityMax')
      ),
    personId: z.string().min(1, translateText('common.fieldRequired')),
    personType: z.enum(['STUDENT', 'STAFF'], {
      required_error: translateText('common.fieldRequired'),
    }),
  });

const AddEditEmergencyContactsView: FC = () => {
  const { translateText } = useTranslation();
  const { selectedContact, editMode, viewState, currentViewType } =
    useHealthAndSafetyState();
  const { setViewState, setEditMode, selectContact } =
    useHealthAndSafetyActions();
  const { createContact, updateContact, isLoading, errorMessage } =
    useHealthAndSafety();

  const isEditMode = editMode === 'edit' && selectedContact;
  const isOpen =
    (viewState === EViewState.ADD || viewState === EViewState.EDIT) &&
    currentViewType === 'emergencyContacts';

  const contactFormSchema = getEmergencyContactFormSchema(translateText);

  const defaultValues: EmergencyContactFormData = {
    name: '',
    relationship: 'PARENT',
    phoneNumber: '',
    alternatePhoneNumber: '',
    email: '',
    address: '',
    isActive: true,
    priority: 1,
    personId: '',
    personType: 'STUDENT',
  };

  const methods = useForm<EmergencyContactFormData>({
    resolver: zodResolver(contactFormSchema),
    mode: 'onBlur',
    defaultValues,
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = methods;

  useEffect(() => {
    if (isEditMode && selectedContact) {
      reset({
        name: selectedContact.name,
        relationship: selectedContact.relationship,
        phoneNumber: selectedContact.phoneNumber,
        alternatePhoneNumber: selectedContact.alternatePhoneNumber || '',
        email: selectedContact.email || '',
        address: selectedContact.address || '',
        isActive: selectedContact.isActive,
        priority: selectedContact.priority,
        personId: selectedContact.personId,
        personType: selectedContact.personType,
      });
    } else if (isOpen && !isEditMode) {
      reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, selectedContact, isOpen, reset]);

  const handleClose = () => {
    if (isEditMode) {
      // Return to details view when canceling from edit mode
      setViewState(EViewState.DETAILS);
    } else {
      // Return to list view when canceling from add mode
      setViewState(EViewState.LIST);
      selectContact(undefined);
    }
    setEditMode(undefined);
    reset(defaultValues);
  };

  const onSubmitForm = async (data: EmergencyContactFormData) => {
    try {
      const contactData: Omit<
        TEmergencyContact,
        'id' | 'createdAt' | 'updatedAt'
      > = {
        name: data.name,
        relationship: data.relationship,
        phoneNumber: data.phoneNumber,
        alternatePhoneNumber: data.alternatePhoneNumber || undefined,
        email: data.email || undefined,
        address: data.address || undefined,
        isActive: data.isActive,
        priority: data.priority,
        personId: data.personId,
        personType: data.personType,
      };

      if (isEditMode && selectedContact) {
        await updateContact(selectedContact.id, contactData);
      } else {
        await createContact(contactData);
      }

      handleClose();
    } catch (error) {
      console.error('Error saving emergency contact:', error);
    }
  };

  const relationshipOptions: TKeyValue<string>[] = [
    {
      key: 'PARENT',
      value:
        translateText('administration.healthAndSafety.values.parent') ||
        'Parent',
    },
    {
      key: 'GUARDIAN',
      value:
        translateText('administration.healthAndSafety.values.guardian') ||
        'Guardian',
    },
    {
      key: 'EMERGENCY_CONTACT',
      value:
        translateText(
          'administration.healthAndSafety.values.emergencyContact'
        ) || 'Emergency Contact',
    },
    {
      key: 'DOCTOR',
      value:
        translateText('administration.healthAndSafety.values.doctor') ||
        'Doctor',
    },
    {
      key: 'HOSPITAL',
      value:
        translateText('administration.healthAndSafety.values.hospital') ||
        'Hospital',
    },
  ];

  const personTypeOptions: TKeyValue<string>[] = [
    {
      key: 'STUDENT',
      value:
        translateText('administration.healthAndSafety.values.student') ||
        'Student',
    },
    {
      key: 'STAFF',
      value:
        translateText('administration.healthAndSafety.values.staff') || 'Staff',
    },
  ];

  const priorityOptions: TKeyValue<string | number>[] = Array.from(
    { length: 10 },
    (_, i) => ({
      key: i + 1,
      value:
        i + 1 === 1
          ? translateText('administration.healthAndSafety.values.primary') ||
          'Primary'
          : `${translateText('administration.healthAndSafety.values.secondary') || 'Secondary'} (${i + 1})`,
    })
  );

  return (
    <div data-testid="add-edit-emergency-contacts-container">
      <StructuredModal
        isOpen={isOpen}
        onClose={handleClose}
        title={
          isEditMode
            ? translateText(
              'administration.healthAndSafety.editEmergencyContacts'
            ) || 'Edit Emergency Contact'
            : translateText(
              'administration.healthAndSafety.addEmergencyContacts'
            ) || 'Add Emergency Contact'
        }
        drawerWidth="80vw"
        footerPositiveAction={handleSubmit(onSubmitForm)}
        footerPositiveActionText={
          isEditMode
            ? translateText('common.update')
            : translateText('common.save')
        }
        isLoading={isLoading}
        footerNegativeAction={handleClose}
        footerNegativeActionText={translateText('common.cancel')}
        footerMessage={
          errorMessage ? (
            <Typography
              color="error"
              variant="body2"
              data-testid="emergency-contact-error-message"
            >
              {errorMessage}
            </Typography>
          ) : null
        }
      >
        <FormProvider {...methods}>
          <form
            onSubmit={handleSubmit(onSubmitForm)}
            style={{ width: '100%' }}
            data-testid="emergency-contact-form"
          >
            <Grid container spacing={3} padding={2}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  {translateText(
                    'administration.healthAndSafety.labels.contactInformation'
                  )}
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} md={6}>
                <ControlledTextField
                  id="name"
                  label={translateText(
                    'administration.healthAndSafety.columns.contactName'
                  )}
                  name="name"
                  control={control}
                  errorMessage={errors.name?.message}
                  required
                  data-testid="contact-name-field"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <ControlledSelectField
                  id="relationship"
                  label={translateText(
                    'administration.healthAndSafety.columns.relationship'
                  )}
                  name="relationship"
                  control={control}
                  itemList={relationshipOptions}
                  errorMessage={errors.relationship?.message}
                  isRequired
                  data-testid="relationship-field"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <ControlledTextField
                  id="phoneNumber"
                  label={translateText(
                    'administration.healthAndSafety.columns.phone'
                  )}
                  name="phoneNumber"
                  control={control}
                  errorMessage={errors.phoneNumber?.message}
                  required
                  data-testid="phone-number-field"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <ControlledTextField
                  id="alternatePhoneNumber"
                  label={translateText(
                    'administration.healthAndSafety.labels.alternatePhone'
                  )}
                  name="alternatePhoneNumber"
                  control={control}
                  errorMessage={errors.alternatePhoneNumber?.message}
                  data-testid="alternate-phone-field"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <ControlledTextField
                  id="email"
                  label={translateText(
                    'administration.healthAndSafety.columns.email'
                  )}
                  name="email"
                  type="email"
                  control={control}
                  errorMessage={errors.email?.message}
                  data-testid="email-field"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <ControlledSelectField
                  id="priority"
                  label={translateText(
                    'administration.healthAndSafety.columns.priority'
                  )}
                  name="priority"
                  control={control}
                  itemList={priorityOptions}
                  errorMessage={errors.priority?.message}
                  isRequired
                  data-testid="priority-field"
                />
              </Grid>

              <Grid item xs={12}>
                <ControlledTextField
                  id="address"
                  label={translateText(
                    'administration.healthAndSafety.labels.address'
                  )}
                  name="address"
                  control={control}
                  errorMessage={errors.address?.message}
                  multiline
                  rows={2}
                  data-testid="address-field"
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  {translateText(
                    'administration.healthAndSafety.labels.associatedInformation'
                  )}
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} md={6}>
                <ControlledTextField
                  id="personId"
                  label={translateText(
                    'administration.healthAndSafety.columns.personId'
                  )}
                  name="personId"
                  control={control}
                  errorMessage={errors.personId?.message}
                  required
                  data-testid="person-id-field"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <ControlledSelectField
                  id="personType"
                  label={translateText(
                    'administration.healthAndSafety.columns.personType'
                  )}
                  name="personType"
                  control={control}
                  itemList={personTypeOptions}
                  errorMessage={errors.personType?.message}
                  isRequired
                  data-testid="person-type-field"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={methods.watch('isActive')}
                      onChange={(e) =>
                        methods.setValue('isActive', e.target.checked)
                      }
                      data-testid="is-active-switch"
                    />
                  }
                  label={translateText(
                    'administration.healthAndSafety.status.active'
                  )}
                />
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </StructuredModal>
    </div>
  );
};

export default AddEditEmergencyContactsView;
